import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import PersonAddAltOutlinedIcon from '@mui/icons-material/PersonAddAltOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  cancelTrainingChangeRequest,
  createStatisticsTrainingChangeRequests,
  fullEditTrainingEntry,
  getStatisticsPendingRequestContext,
  getStatisticsTrainingRequestContext,
  getTrainingEntryEditContext,
  updateStatisticsPendingRequest,
} from '../api/training';
import { downloadDienstzeitProtokollForEntry } from './pdf/dienstzeitPdfExport';
import DateInputField from './DateInputField';
import { getResponsibleQuestion, getTimeQuestion } from '../config/logbookEntryWizardSchema';
import { calculateRawServiceHours, getLogbookEntryTypeRule, getPauseHoursForDuration } from '../config/logbookEntryRules';
import {
  getAvailableDayServiceCodeGroups,
  isLogbookEntryEditWorkflowComplete,
  resolveDayServiceVariant,
} from '../config/logbookEntryEditWizardModel';

const A3_TIME_FROM = '07:00';
const A3_TIME_TO = '15:42';
const EXTERNAL_TRAINING_ENTRY_TYPE = 'Externe Fortbildung';

function decimalToDuration(value) {
  const totalMinutes = Math.round(Number(value || 0) * 60);
  return `${String(Math.floor(totalMinutes / 60)).padStart(2, '0')}:${String(totalMinutes % 60).padStart(2, '0')}`;
}

function durationToDecimal(value) {
  const match = String(value || '').trim().match(/^(\d{1,2}):(\d{2})$/);
  if (!match || Number(match[2]) > 59) return null;
  return Number(((Number(match[1]) * 60 + Number(match[2])) / 60).toFixed(2));
}

function getDurationParts(value) {
  const [hours = '0', minutes = '00'] = String(value || '00:00').split(':');
  return { hours: String(Number(hours || 0)), minutes: String(minutes || '00').padStart(2, '0') };
}

function timingFromOriginal(original = {}) {
  return {
    hours: decimalToDuration(original.hours),
    timeFrom: original.timeFrom || '',
    timeTo: original.timeTo || '',
  };
}

function inferDayServiceMode(entry) {
  return entry?.timeFrom === A3_TIME_FROM
    && entry?.timeTo === A3_TIME_TO
    && entry?.pauseTaken
    ? 'full-day'
    : 'individual';
}

function LogbookEntryEditDialog({ entryId, open, onClose, onSaved, onUpdated, requestScope = null }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const isPendingCreateRequest = requestScope === 'pending-create';
  const isStatisticsRequest = requestScope === 'single' || requestScope === 'group' || isPendingCreateRequest;
  const [context, setContext] = useState(null);
  const [form, setForm] = useState(null);
  const [targets, setTargets] = useState([]);
  const [newTargets, setNewTargets] = useState([]);
  const [newTargetCandidate, setNewTargetCandidate] = useState(null);
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [exportingPdf, setExportingPdf] = useState(false);
  const [savedForPdfRetry, setSavedForPdfRetry] = useState(false);
  const [cancellingRequestId, setCancellingRequestId] = useState(null);
  const [contextReloadKey, setContextReloadKey] = useState(0);
  const [row4ServiceMode, setRow4ServiceMode] = useState('');
  const [error, setError] = useState('');
  const [keywordInput, setKeywordInput] = useState('');
  const preservedReloadError = useRef('');

  useEffect(() => {
    if (!open || !entryId) return;
    let active = true;
    setLoading(true);
    setError(preservedReloadError.current);
    preservedReloadError.current = '';
    setSavedForPdfRetry(false);
    const contextRequest = isPendingCreateRequest
      ? getStatisticsPendingRequestContext(entryId)
      : isStatisticsRequest
      ? getStatisticsTrainingRequestContext(entryId)
      : getTrainingEntryEditContext(entryId);
    contextRequest
      .then(({ data }) => {
        if (!active) return;
        const entry = data.entry;
        const entryTypeMeta = (data.activityTypes || []).find((option) => option.value === entry.entryType);
        const entryRule = getLogbookEntryTypeRule(entry.entryType, entryTypeMeta);
        const initialServiceMode = inferDayServiceMode(entry);
        const initialCodeGroups = getAvailableDayServiceCodeGroups(entryTypeMeta, entryRule.row4Mode);
        setContext(isStatisticsRequest
          ? { ...data, canManageGroup: requestScope === 'group', canAddGroupMembers: false, availableGroupUsers: [] }
          : data);
        setForm({
          date: entry.date || '',
          description: entry.description || '',
          entryType: entry.entryType || '',
          entryVariant: entry.entryVariant || (initialCodeGroups.length === 1
            ? resolveDayServiceVariant(initialCodeGroups[0], initialServiceMode)
            : ''),
          responsibleMode: entry.responsibleMode || 'internal',
          responsibleId: entry.responsibleId || '',
          responsibleNameSnapshot: entry.responsibleNameSnapshot || '',
          responsibleCompanySnapshot: entry.responsibleCompanySnapshot || '',
          responsibleIsLehrhSnapshot: Boolean(entry.responsibleIsLehrhSnapshot),
          hours: decimalToDuration(entry.hours),
          timeFrom: entry.timeFrom || '',
          timeTo: entry.timeTo || '',
          pauseTaken: entry.pauseTaken,
          pauseAnswer: entry.pauseTaken ? 'ja' : 'nein',
          pauseHours: decimalToDuration(entry.pauseHours),
          keywords: (entry.keywords || []).join(', '),
        });
        setRow4ServiceMode(initialServiceMode);
        const contextTargets = isStatisticsRequest ? data.targets : data.groupTargets;
        setTargets((requestScope === 'single' ? [] : (contextTargets || [])).map((target) => ({
          ...target,
          targetKind: target.targetKind || 'entry',
          selected: false,
          ...timingFromOriginal(target.original),
        })));
        setNewTargets([]);
        setNewTargetCandidate(null);
        setReason('');
      })
      .catch((loadError) => {
        setContext(null);
        setForm(null);
        setError(loadError.response?.data?.error || 'Bearbeitungsdaten konnten nicht geladen werden.');
      })
      .finally(() => active && setLoading(false));
    return () => { active = false; };
  }, [contextReloadKey, entryId, isPendingCreateRequest, isStatisticsRequest, open, requestScope]);

  const updateForm = (name, value) => setForm((current) => ({ ...current, [name]: value }));
  const updateCommonTime = (name, value) => {
    updateForm(name, value);
    if (isStatisticsRequest && requestScope === 'group') {
      setTargets((current) => current.map((target) => ({ ...target, [name]: value })));
    }
  };
  const selectPreset24h = () => {
    setForm((current) => ({ ...current, entryVariant: 'preset24h', timeFrom: '09:00', timeTo: '12:30' }));
    if (isStatisticsRequest && requestScope === 'group') {
      setTargets((current) => current.map((target) => ({ ...target, timeFrom: '09:00', timeTo: '12:30' })));
    }
  };
  const updateHoursPart = (part, value) => {
    const digits = String(value || '').replace(/\D/g, '');
    const currentParts = getDurationParts(form?.hours);
    const nextHours = part === 'hours' ? Math.min(Number(digits || 0), 99) : Number(currentParts.hours);
    const nextMinutes = part === 'minutes' ? Math.min(Number(digits || 0), 59) : Number(currentParts.minutes);
    updateForm('hours', `${String(nextHours).padStart(2, '0')}:${String(nextMinutes).padStart(2, '0')}`);
  };

  const addKeyword = () => {
    const keyword = keywordInput.trim();
    if (!keyword) return;
    const keywords = form.keywords.split(',').map((entry) => entry.trim()).filter(Boolean);
    if (!keywords.some((entry) => entry.toLocaleLowerCase('de-DE') === keyword.toLocaleLowerCase('de-DE'))) {
      updateForm('keywords', [...keywords, keyword].join(', '));
    }
    setKeywordInput('');
  };

  const removeKeyword = (keyword) => updateForm('keywords', form.keywords
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry && entry !== keyword)
    .join(', '));
  const handleEntryTypeChange = (entryType) => {
    const entryTypeMeta = (context?.activityTypes || []).find((option) => option.value === entryType);
    const rule = getLogbookEntryTypeRule(entryType, entryTypeMeta);
    setRow4ServiceMode('');
    setForm((current) => ({
      ...current,
      entryType,
      entryVariant: rule.row4Mode === 'custom-only' ? 'customOnly' : '',
      responsibleMode: entryType === EXTERNAL_TRAINING_ENTRY_TYPE ? current.responsibleMode : 'internal',
      responsibleId: rule.autoResponsibleCurrentUser ? Number(context?.currentUserId) : current.responsibleId,
      responsibleNameSnapshot: entryType === EXTERNAL_TRAINING_ENTRY_TYPE ? current.responsibleNameSnapshot : '',
      responsibleCompanySnapshot: entryType === EXTERNAL_TRAINING_ENTRY_TYPE ? current.responsibleCompanySnapshot : '',
      timeFrom: '',
      timeTo: '',
      pauseTaken: false,
      pauseAnswer: '',
      pauseHours: '00:00',
      hours: rule.autoHoursZero ? '00:00' : current.hours,
      keywords: '',
    }));
    if (isStatisticsRequest && requestScope === 'group') {
      setTargets((current) => current.map((target) => ({
        ...target,
        timeFrom: '',
        timeTo: '',
        hours: rule.autoHoursZero ? '00:00' : target.hours,
      })));
    }
  };

  const handleDayServiceModeChange = (mode) => {
    setRow4ServiceMode(mode);
    setForm((current) => ({
      ...current,
      entryVariant: dayServiceCodeGroups.length === 1
        ? resolveDayServiceVariant(dayServiceCodeGroups[0], mode)
        : '',
      timeFrom: mode === 'full-day' ? A3_TIME_FROM : '',
      timeTo: mode === 'full-day' ? A3_TIME_TO : '',
      pauseTaken: mode === 'full-day',
      pauseAnswer: mode === 'full-day' ? 'ja' : '',
      pauseHours: mode === 'full-day' ? '00:30' : '00:00',
    }));
    if (isStatisticsRequest && requestScope === 'group') {
      setTargets((current) => current.map((target) => ({
        ...target,
        timeFrom: mode === 'full-day' ? A3_TIME_FROM : '',
        timeTo: mode === 'full-day' ? A3_TIME_TO : '',
      })));
    }
  };

  const handlePauseAnswerChange = (pauseAnswer) => {
    setForm((current) => ({
      ...current,
      pauseAnswer,
      pauseTaken: pauseAnswer === 'ja',
      pauseHours: pauseAnswer === 'ja'
        ? decimalToDuration(getPauseHoursForDuration(calculateRawServiceHours(current.timeFrom, current.timeTo), 'ja'))
        : '00:00',
    }));
  };

  const handleVariantChange = (entryVariant) => {
    setForm((current) => ({
      ...current,
      entryVariant,
      timeFrom: entryVariant === 'a3' ? A3_TIME_FROM : current.timeFrom,
      timeTo: entryVariant === 'a3' ? A3_TIME_TO : current.timeTo,
      pauseTaken: entryVariant === 'a3' ? true : current.pauseTaken,
      pauseHours: entryVariant === 'a3' ? '00:30' : current.pauseHours,
    }));
    if (isStatisticsRequest && requestScope === 'group' && entryVariant === 'a3') {
      setTargets((current) => current.map((target) => ({
        ...target,
        timeFrom: A3_TIME_FROM,
        timeTo: A3_TIME_TO,
      })));
    }
  };
  const updateTarget = (index, changes) => setTargets((current) => current.map((target, targetIndex) => (
    targetIndex === index ? { ...target, ...changes } : target
  )));

  const handleAddTarget = () => {
    if (!newTargetCandidate) return;
    setNewTargets((current) => [...current, {
      userId: Number(newTargetCandidate.id),
      fullName: newTargetCandidate.full_name,
      hours: form.hours,
      timeFrom: form.timeFrom,
      timeTo: form.timeTo,
    }]);
    setNewTargetCandidate(null);
  };

  const updateNewTarget = (index, changes) => setNewTargets((current) => current.map((target, targetIndex) => (
    targetIndex === index ? { ...target, ...changes } : target
  )));

  const handleCancelRequest = async (requestId) => {
    setCancellingRequestId(requestId);
    setError('');
    try {
      await cancelTrainingChangeRequest(requestId, 'Vom Antragsteller zurückgezogen');
      const { data } = await getTrainingEntryEditContext(entryId);
      setContext(data);
      setTargets((data.groupTargets || []).map((target) => ({
        ...target,
        selected: false,
        ...timingFromOriginal(target.original),
      })));
    } catch (cancelError) {
      setError(cancelError.response?.data?.error || 'Anfrage konnte nicht zurückgezogen werden.');
    } finally {
      setCancellingRequestId(null);
    }
  };

  const handlePdfExport = async () => {
    setExportingPdf(true);
    setError('');
    try {
      await downloadDienstzeitProtokollForEntry(entryId);
      onSaved();
    } catch (pdfError) {
      setError(`Der Eintrag wurde gespeichert, aber das Stundenprotokoll konnte nicht heruntergeladen werden: ${pdfError.message}`);
      setSavedForPdfRetry(true);
    } finally {
      setExportingPdf(false);
    }
  };

  const handleSave = async (createPdf = false) => {
    const ownHours = durationToDecimal(form.hours);
    const pauseHours = form.pauseTaken ? durationToDecimal(form.pauseHours) : 0;
    if (ownHours === null || pauseHours === null) {
      setError('Stunden und Pause müssen im Format HH:MM angegeben werden.');
      return;
    }
    const selectedTargets = targets.filter((target) => target.selected);
    const linkedUpdates = [];
    for (const target of selectedTargets) {
      const targetHours = durationToDecimal(target.hours);
      if (targetHours === null) {
        setError(`Ungültige Stunden für ${target.fullName}.`);
        return;
      }
      linkedUpdates.push({
        targetKind: target.targetKind,
        entryId: target.entryId,
        requestId: target.requestId,
        requestRevision: target.requestRevision,
        hours: targetHours,
        timeFrom: target.timeFrom || null,
        timeTo: target.timeTo || null,
      });
    }
    const newEntries = [];
    for (const target of newTargets) {
      const targetHours = durationToDecimal(target.hours);
      if (targetHours === null) {
        setError(`Ungültige Stunden für ${target.fullName}.`);
        return;
      }
      newEntries.push({
        userId: target.userId,
        hours: targetHours,
        timeFrom: target.timeFrom || null,
        timeTo: target.timeTo || null,
      });
    }

    setSaving(true);
    setError('');
    try {
      const commonFields = {
        date: form.date,
        description: form.description,
        entryType: form.entryType,
        entryVariant: form.entryVariant,
        responsibleMode: form.responsibleMode,
        responsibleId: form.responsibleId || null,
        responsibleNameSnapshot: form.responsibleNameSnapshot,
        responsibleCompanySnapshot: form.responsibleCompanySnapshot,
        responsibleIsLehrhSnapshot: form.responsibleIsLehrhSnapshot,
        pauseTaken: form.pauseTaken,
        pauseHours,
        keywords: form.keywords.split(',').map((keyword) => keyword.trim()).filter(Boolean),
      };
      if (isPendingCreateRequest) {
        await updateStatisticsPendingRequest(entryId, {
          requestRevision: context.requestRevision,
          reason: reason.trim(),
          commonFields,
          ownTiming: {
            hours: ownHours,
            timeFrom: form.timeFrom || null,
            timeTo: form.timeTo || null,
          },
        });
        onSaved(1);
        return;
      }
      if (isStatisticsRequest) {
        await createStatisticsTrainingChangeRequests(entryId, {
          action: 'update',
          scope: requestScope,
          reason: reason.trim(),
          commonFields,
          targets: requestScope === 'group'
            ? linkedUpdates.map((target) => ({
              targetKind: target.targetKind,
              entryId: target.entryId,
              requestId: target.requestId,
              requestRevision: target.requestRevision,
              hours: target.hours,
              timeFrom: target.timeFrom,
              timeTo: target.timeTo,
            }))
            : [{ entryId, hours: ownHours, timeFrom: form.timeFrom || null, timeTo: form.timeTo || null }],
        });
        onSaved(requestScope === 'group' ? selectedTargets.length : 1);
        return;
      }
      await fullEditTrainingEntry(entryId, {
        commonFields,
        ownTiming: {
          hours: ownHours,
          timeFrom: form.timeFrom || null,
          timeTo: form.timeTo || null,
        },
        linkedUpdates,
        newEntries,
        reason,
      });
      if (!createPdf) {
        onSaved();
        return;
      }

      setSavedForPdfRetry(true);
      onUpdated?.();
      await handlePdfExport();
    } catch (saveError) {
      const message = saveError.response?.data?.error || 'Eintrag konnte nicht gespeichert werden.';
      setError(message);
      if (isStatisticsRequest && saveError.response?.status === 409) {
        preservedReloadError.current = message;
        setContext(null);
        setForm(null);
        setTargets([]);
        setContextReloadKey((current) => current + 1);
      }
    } finally {
      setSaving(false);
    }
  };

  const selectedTargetCount = targets.filter((target) => target.selected).length;
  const singleRequestTarget = isStatisticsRequest && requestScope === 'single'
    ? context?.targets?.find((target) => Number(target.entryId) === Number(entryId))
    : null;
  const singleRequestAvailable = !singleRequestTarget || singleRequestTarget.canSelect;
  const groupActionCount = selectedTargetCount + newTargets.length;
  const externalResponsible = form?.responsibleMode === 'external';
  const selectedEntryTypeMeta = (context?.activityTypes || []).find((option) => option.value === form?.entryType);
  const canCreateProtocolPdf = !isStatisticsRequest && selectedEntryTypeMeta?.pdfProtocolMode === 'ask'
    && (!context?.entryGroupId || context?.canManageGroup);
  const selectedRule = getLogbookEntryTypeRule(form?.entryType, selectedEntryTypeMeta);
  const row4Mode = selectedRule.row4Mode;
  const dayServiceCodeGroups = getAvailableDayServiceCodeGroups(selectedEntryTypeMeta, row4Mode);
  const isDayServiceWizardFlow = ['a3-za3', 'te1-flex-honorar'].includes(row4Mode)
    && dayServiceCodeGroups.length > 0;
  const shouldShowDayServiceCodeQuestion = isDayServiceWizardFlow && dayServiceCodeGroups.length > 1;
  const dayServiceCodeComplete = !isDayServiceWizardFlow
    || dayServiceCodeGroups.length === 1
    || Boolean(form?.entryVariant);
  const availableNewTargetOptions = (context?.availableGroupUsers || []).filter((candidate) => (
    !newTargets.some((target) => Number(target.userId) === Number(candidate.id))
  ));
  const supportsExternalResponsible = form?.entryType === EXTERNAL_TRAINING_ENTRY_TYPE;
  const selectedResponsible = (context?.responsibles || []).find((person) => Number(person.id) === Number(form?.responsibleId)) || null;
  const responsibleComplete = selectedRule.autoResponsibleCurrentUser
    || (externalResponsible
      ? Boolean(form?.responsibleNameSnapshot.trim() && form?.responsibleCompanySnapshot.trim())
      : Boolean(form?.responsibleId));
  const customTimeVisible = row4Mode === 'custom-only'
    || form?.entryVariant === 'custom24h'
    || form?.entryVariant === 'za3'
    || form?.entryVariant === 'te1te2-flex'
    || form?.entryVariant === 'honorar-flex';
  const timeComplete = Boolean(form?.timeFrom && form?.timeTo);
  const serviceHours = timeComplete ? calculateRawServiceHours(form.timeFrom, form.timeTo) : null;
  const showPauseQuestion = Boolean(selectedRule.showPauseQuestion && timeComplete && serviceHours >= 6);
  const row4PauseComplete = !isDayServiceWizardFlow || row4ServiceMode === 'full-day' || Boolean(form?.pauseAnswer);
  const timePathComplete = timeComplete && row4PauseComplete;
  const nonDayPauseComplete = isDayServiceWizardFlow || !showPauseQuestion || Boolean(form?.pauseAnswer);
  const hideHours = selectedRule.autoHoursZero || selectedEntryTypeMeta?.hoursQuestionMode === 'hidden';
  const keywordMode = selectedEntryTypeMeta?.keywordMode || 'optional';
  const showKeywords = keywordMode !== 'hidden';
  const keywords = form?.keywords.split(',').map((entry) => entry.trim()).filter(Boolean) || [];
  const keywordsComplete = keywordMode !== 'required' || keywords.length > 0;
  const row4VariantComplete = isDayServiceWizardFlow
    ? Boolean(row4ServiceMode)
    : row4Mode === 'custom-only' || Boolean(form?.entryVariant);
  const hoursComplete = hideHours || durationToDecimal(form?.hours) !== null;
  const workflowComplete = isLogbookEntryEditWorkflowComplete({
    date: form?.date,
    entryType: form?.entryType,
    responsibleComplete,
    row4VariantComplete,
    timeComplete: timePathComplete,
    pauseComplete: nonDayPauseComplete,
    hoursComplete,
    keywordsComplete,
    dayServiceCodeComplete,
  });

  return (
    <Dialog open={open} onClose={saving || exportingPdf ? undefined : onClose} fullWidth maxWidth="md" fullScreen={fullScreen}>
      <DialogTitle>{isPendingCreateRequest ? 'Offene Eintragsanfrage bearbeiten' : isStatisticsRequest ? 'Änderungsanfrage erstellen' : 'Logbucheintrag bearbeiten'}</DialogTitle>
      <DialogContent dividers>
        {loading && <Box sx={{ display: 'grid', placeItems: 'center', minHeight: 220 }}><CircularProgress /></Box>}
        {!loading && !form && error && <Alert severity="error">{error}</Alert>}
        {!loading && form && (
          <Stack spacing={2}>
            {error && <Alert severity="error">{error}</Alert>}
            {savedForPdfRetry && <Alert severity="success">Die Änderungen am Logbucheintrag wurden gespeichert.</Alert>}
            {isStatisticsRequest && (
              <Alert severity="info">
                {isPendingCreateRequest
                  ? `Die offene Eintragsanfrage für ${context?.target?.fullName || 'die ausgewählte Person'} wird direkt aktualisiert. Antragsteller bleibt ${context?.requester?.fullName || 'die ursprüngliche Person'}.`
                  : requestScope === 'group'
                  ? `Die ausgewählten Änderungen werden einzeln an die Eigentümer der ${context?.groupSize || 0} Gruppeneinträge gesendet.`
                  : `Die Änderung wird als Anfrage an ${context?.owner?.fullName || 'den Eigentümer'} gesendet.`}
              </Alert>
            )}
            {isStatisticsRequest && requestScope === 'single' && !singleRequestAvailable && (
              <Alert severity="warning">Für diesen Eintrag besteht bereits eine offene Änderungs- oder Löschanfrage.</Alert>
            )}

            <Typography variant="body2" color="text.secondary">1. Wann hat die Übung/der Einsatz statt gefunden?</Typography>
            <DateInputField
              fullWidth
              label="Kalenderdatum"
              value={form.date}
              onChange={(value) => updateForm('date', value)}
              helperText="Gespeichertes Datum ändern"
            />

            {form.date && (
              <>
                <Typography variant="body2" color="text.secondary">2. Wähle die Art der Tätigkeit aus</Typography>
                <FormControl fullWidth>
                  <InputLabel>Art der Tätigkeit</InputLabel>
                  <Select label="Art der Tätigkeit" value={form.entryType} onChange={(event) => handleEntryTypeChange(event.target.value)}>
                    {(context?.activityTypes || []).map((option) => <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>)}
                  </Select>
                </FormControl>
              </>
            )}

            {form.entryType && !selectedRule.autoResponsibleCurrentUser && (
              <>
                <Typography variant="body2" color="text.secondary">{getResponsibleQuestion(form.entryType, selectedEntryTypeMeta)}</Typography>
                {supportsExternalResponsible && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <Button variant={!externalResponsible ? 'contained' : 'outlined'} onClick={() => updateForm('responsibleMode', 'internal')}>Mitglied SRHT DUS</Button>
                    <Button variant={externalResponsible ? 'contained' : 'outlined'} onClick={() => updateForm('responsibleMode', 'external')}>Externer Dozent erfassen</Button>
                  </Stack>
                )}
                {externalResponsible ? (
                  <Stack spacing={1.25}>
                    <TextField fullWidth label="Name des Verantwortlichen" value={form.responsibleNameSnapshot} onChange={(event) => updateForm('responsibleNameSnapshot', event.target.value)} />
                    <TextField fullWidth label="Firma / Organisation" value={form.responsibleCompanySnapshot} onChange={(event) => updateForm('responsibleCompanySnapshot', event.target.value)} />
                    <FormControlLabel control={<Checkbox checked={Boolean(form.responsibleIsLehrhSnapshot)} onChange={(event) => updateForm('responsibleIsLehrhSnapshot', event.target.checked)} />} label="Lehrhöhenretterstunden" />
                  </Stack>
                ) : (
                  <Autocomplete
                    fullWidth
                    options={context?.responsibles || []}
                    value={selectedResponsible}
                    getOptionLabel={(person) => person.full_name || ''}
                    isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                    onChange={(event, person) => updateForm('responsibleId', person?.id || '')}
                    renderInput={(params) => <TextField {...params} label="Verantwortliche Person" placeholder="Name suchen" />}
                  />
                )}
              </>
            )}

            {form.entryType && responsibleComplete && (
              <>
                {isDayServiceWizardFlow ? (
                  <>
                    <Typography variant="body2" color="text.secondary">4. Soll ein vollständiger Tagesdienst oder ein individueller Zeitraum erfasst werden?</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                      <Button variant={row4ServiceMode === 'full-day' ? 'contained' : 'outlined'} onClick={() => handleDayServiceModeChange('full-day')}>Vollständiger Tagesdienst</Button>
                      <Button variant={row4ServiceMode === 'individual' ? 'contained' : 'outlined'} onClick={() => handleDayServiceModeChange('individual')}>Individueller Zeitraum</Button>
                    </Stack>
                    {row4ServiceMode === 'full-day' && <Alert severity="info">Zeitraum fest gesetzt: 07:00 bis 15:42 Uhr. Pause ist automatisch auf „Ja“ gesetzt.</Alert>}
                    {row4ServiceMode === 'individual' && (
                      <>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                          <TextField fullWidth type="time" label="Von" value={form.timeFrom} onInput={(event) => updateCommonTime('timeFrom', event.target.value)} onChange={(event) => updateCommonTime('timeFrom', event.target.value)} InputLabelProps={{ shrink: true }} />
                          <TextField fullWidth type="time" label="Bis" value={form.timeTo} onInput={(event) => updateCommonTime('timeTo', event.target.value)} onChange={(event) => updateCommonTime('timeTo', event.target.value)} InputLabelProps={{ shrink: true }} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary">4b. Wurden Pausen gemacht?</Typography>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                          <Button variant={form.pauseAnswer === 'ja' ? 'contained' : 'outlined'} onClick={() => handlePauseAnswerChange('ja')}>Ja</Button>
                          <Button variant={form.pauseAnswer === 'nein' ? 'contained' : 'outlined'} onClick={() => handlePauseAnswerChange('nein')}>Nein</Button>
                        </Stack>
                      </>
                    )}
                  </>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary">{getTimeQuestion(form.entryType, selectedEntryTypeMeta)}</Typography>
                {row4Mode === 'preset-or-custom' && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <Button variant={form.entryVariant === 'preset24h' ? 'contained' : 'outlined'} onClick={selectPreset24h}>09:00 Uhr bis 12:30 Uhr</Button>
                    <Button variant={form.entryVariant === 'custom24h' ? 'contained' : 'outlined'} onClick={() => handleVariantChange('custom24h')}>Individueller Zeitraum</Button>
                  </Stack>
                )}
                {(customTimeVisible || row4Mode === 'custom-only') && (
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <TextField fullWidth type="time" label="Von" value={form.timeFrom} onInput={(event) => updateCommonTime('timeFrom', event.target.value)} onChange={(event) => updateCommonTime('timeFrom', event.target.value)} InputLabelProps={{ shrink: true }} />
                    <TextField fullWidth type="time" label="Bis" value={form.timeTo} onInput={(event) => updateCommonTime('timeTo', event.target.value)} onChange={(event) => updateCommonTime('timeTo', event.target.value)} InputLabelProps={{ shrink: true }} />
                  </Stack>
                )}
                  </>
                )}
                {timeComplete && <Typography variant="body2" color="text.secondary">{selectedRule.serviceDurationLabel}: {decimalToDuration(serviceHours)} h</Typography>}
              </>
            )}

            {timePathComplete && !hideHours && (
              <>
                <Typography variant="body2" color="text.secondary">5. Wie viele Höhenretterstunden wurden absolviert?</Typography>
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.5 }}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                    <TextField fullWidth label="Stunden" value={getDurationParts(form.hours).hours} onChange={(event) => updateHoursPart('hours', event.target.value)} inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} />
                    <TextField fullWidth label="Minuten" value={getDurationParts(form.hours).minutes} onChange={(event) => updateHoursPart('minutes', event.target.value)} inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }} />
                  </Stack>
                </Card>
              </>
            )}

            {timePathComplete && showKeywords && (
              <>
                <Typography variant="body2" color="text.secondary">5a. Welche Schlagwörter beschreiben den Inhalt der Veranstaltung?{keywordMode === 'required' ? ' (Pflichtfeld)' : ''}</Typography>
                <Card variant="outlined" sx={{ borderRadius: 2, p: 1.25 }}>
                  <Stack spacing={1}>
                    <TextField
                      size="small"
                      fullWidth
                      label="Schlagwort eingeben und Enter drücken"
                      value={keywordInput}
                      onChange={(event) => setKeywordInput(event.target.value)}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          addKeyword();
                        }
                      }}
                      helperText={`${keywords.length}/20 Schlagwörter`}
                    />
                    <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap>
                      {keywords.map((keyword) => <Chip key={keyword} size="small" color="primary" label={keyword} onDelete={() => removeKeyword(keyword)} />)}
                    </Stack>
                    {keywordMode === 'required' && !keywords.length && <Typography variant="caption" color="warning.main">Bitte mindestens ein Schlagwort hinterlegen.</Typography>}
                  </Stack>
                </Card>
              </>
            )}

            {!isDayServiceWizardFlow && showPauseQuestion && (
              <>
                <Typography variant="body2" color="text.secondary">6. Wurden Pausen gemacht?</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  <Button variant={form.pauseAnswer === 'ja' ? 'contained' : 'outlined'} onClick={() => handlePauseAnswerChange('ja')}>Ja</Button>
                  <Button variant={form.pauseAnswer === 'nein' ? 'contained' : 'outlined'} onClick={() => handlePauseAnswerChange('nein')}>Nein</Button>
                </Stack>
              </>
            )}

            {timePathComplete && shouldShowDayServiceCodeQuestion && (
              <>
                <Typography variant="body2" color="text.secondary">6. Welche Diensteintragung soll verwendet werden?</Typography>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25}>
                  {dayServiceCodeGroups.includes('A') && <Button variant={['a3', 'za3'].includes(form.entryVariant) ? 'contained' : 'outlined'} onClick={() => handleVariantChange(resolveDayServiceVariant('A', row4ServiceMode))}>A3 / zA3</Button>}
                  {dayServiceCodeGroups.includes('T') && <Button variant={String(form.entryVariant).startsWith('te1te2') ? 'contained' : 'outlined'} onClick={() => handleVariantChange(resolveDayServiceVariant('T', row4ServiceMode))}>T (T1/T2 automatisch)</Button>}
                  {dayServiceCodeGroups.includes('HONORAR') && <Button variant={String(form.entryVariant).startsWith('honorar') ? 'contained' : 'outlined'} onClick={() => handleVariantChange(resolveDayServiceVariant('HONORAR', row4ServiceMode))}>Honorar</Button>}
                </Stack>
              </>
            )}

            {timePathComplete && (
              <TextField fullWidth multiline minRows={2} label="Beschreibung (optional)" value={form.description} onChange={(event) => updateForm('description', event.target.value)} />
            )}

            {workflowComplete && context?.canManageGroup && (targets.length > 0 || context?.canAddGroupMembers) && (
              <>
                <Typography variant="body2" color="text.secondary">{shouldShowDayServiceCodeQuestion ? '7' : '6'}. Sollen die Änderungen auch für weitere zugehörige Logbücher angefragt werden?</Typography>
                <Stack spacing={1.5}>
                  {targets.map((target, index) => (
                    <Box key={`${target.targetKind}-${target.entryId || target.requestId}`} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                      <FormControlLabel
                        control={<Checkbox checked={target.selected} disabled={!target.canSelect} onChange={(event) => updateTarget(index, { selected: event.target.checked })} />}
                        label={target.fullName}
                      />
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                        {`Original: ${decimalToDuration(target.original.hours)} h, ${target.original.timeFrom || '--:--'} bis ${target.original.timeTo || '--:--'}${target.status === 'pending-create' ? ' · Eintrag noch nicht freigegeben' : target.status === 'pending-update' ? ' · Änderungsanfrage bereits offen' : ''}`}
                      </Typography>
                      {target.pendingRequestId && !isStatisticsRequest && (
                        <Button
                          size="small"
                          color="warning"
                          startIcon={<UndoIcon />}
                          disabled={cancellingRequestId === target.pendingRequestId}
                          onClick={() => handleCancelRequest(target.pendingRequestId)}
                          sx={{ mb: 1 }}
                        >
                          Offene Änderungsanfrage zurückziehen
                        </Button>
                      )}
                      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                        <TextField size="small" fullWidth label="Stunden (HH:MM)" value={target.hours} disabled={!target.selected} onChange={(event) => updateTarget(index, { hours: event.target.value })} />
                        <TextField size="small" fullWidth type="time" label="Von" value={target.timeFrom} disabled={!target.selected} onChange={(event) => updateTarget(index, { timeFrom: event.target.value })} InputLabelProps={{ shrink: true }} />
                        <TextField size="small" fullWidth type="time" label="Bis" value={target.timeTo} disabled={!target.selected} onChange={(event) => updateTarget(index, { timeTo: event.target.value })} InputLabelProps={{ shrink: true }} />
                      </Stack>
                    </Box>
                  ))}
                </Stack>
                {context?.canAddGroupMembers && (
                  <Stack spacing={1.25}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Weitere Höhenretter nachtragen</Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
                      <Autocomplete
                        fullWidth
                        options={availableNewTargetOptions}
                        value={newTargetCandidate}
                        getOptionLabel={(person) => person.full_name || ''}
                        isOptionEqualToValue={(option, value) => Number(option.id) === Number(value.id)}
                        onChange={(event, person) => setNewTargetCandidate(person)}
                        renderInput={(params) => <TextField {...params} label="Höhenretter suchen" />}
                      />
                      <Button variant="outlined" startIcon={<PersonAddAltOutlinedIcon />} disabled={!newTargetCandidate} onClick={handleAddTarget} sx={{ whiteSpace: 'nowrap', minHeight: 44 }}>
                        Hinzufügen
                      </Button>
                    </Stack>
                    {newTargets.map((target, index) => (
                      <Box key={`new-target-${target.userId}`} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                          <Typography variant="subtitle2">{target.fullName}</Typography>
                          <IconButton size="small" color="error" aria-label={`${target.fullName} entfernen`} onClick={() => setNewTargets((current) => current.filter((item) => item.userId !== target.userId))}>
                            <RemoveCircleOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                          <TextField size="small" fullWidth label="Stunden (HH:MM)" value={target.hours} onChange={(event) => updateNewTarget(index, { hours: event.target.value })} />
                          <TextField size="small" fullWidth type="time" label="Von" value={target.timeFrom} onChange={(event) => updateNewTarget(index, { timeFrom: event.target.value })} InputLabelProps={{ shrink: true }} />
                          <TextField size="small" fullWidth type="time" label="Bis" value={target.timeTo} onChange={(event) => updateNewTarget(index, { timeTo: event.target.value })} InputLabelProps={{ shrink: true }} />
                        </Stack>
                      </Box>
                    ))}
                  </Stack>
                )}
                {groupActionCount > 0 && <TextField required fullWidth multiline minRows={2} label="Begründung für Änderungsanfragen" value={reason} onChange={(event) => setReason(event.target.value)} />}
              </>
            )}
            {isStatisticsRequest && (requestScope === 'single' || isPendingCreateRequest) && (
              <TextField required fullWidth multiline minRows={2} label="Begründung für die Änderungsanfrage" value={reason} onChange={(event) => setReason(event.target.value)} />
            )}
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={saving || exportingPdf}>{savedForPdfRetry ? 'Schließen' : 'Abbrechen'}</Button>
        {!savedForPdfRetry && (
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveOutlinedIcon />}
            onClick={() => handleSave(false)}
            disabled={loading
              || saving
              || exportingPdf
              || !form
              || (isPendingCreateRequest && !context)
              || !workflowComplete
              || (isStatisticsRequest && requestScope === 'single' && !singleRequestAvailable)
              || (isStatisticsRequest && !reason.trim())
              || (isStatisticsRequest && requestScope === 'group' && selectedTargetCount === 0)
              || (!isStatisticsRequest && groupActionCount > 0 && !reason.trim())}
          >
            {isStatisticsRequest
              ? isPendingCreateRequest
                ? 'Eintragsanfrage aktualisieren'
                : `${requestScope === 'group' ? selectedTargetCount : 1} Änderungsanfrage${requestScope === 'group' && selectedTargetCount !== 1 ? 'n' : ''} senden`
              : 'Speichern'}
          </Button>
        )}
        {canCreateProtocolPdf && (
          <Button
            variant={savedForPdfRetry ? 'contained' : 'outlined'}
            startIcon={saving || exportingPdf ? <CircularProgress size={18} color="inherit" /> : <PictureAsPdfOutlinedIcon />}
            onClick={savedForPdfRetry ? handlePdfExport : () => handleSave(true)}
            disabled={loading || saving || exportingPdf || !form || (!savedForPdfRetry && (!workflowComplete || (groupActionCount > 0 && !reason.trim())))}
          >
            {savedForPdfRetry ? 'PDF erneut herunterladen' : 'Speichern & Stundenprotokoll erstellen'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
}

export default LogbookEntryEditDialog;
